import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Stage, Layer, Group, Image as KonvaImage, Rect } from 'react-konva';
import { KonvaEventObject } from 'konva/lib/Node';
import PolygonLayer from './PolygonLayer';
import CreationOverlay from './CreationOverlay';
import { Area, Rack, Shelf, Table, CheckoutZoneData, PolygonEntityType } from '../../app/appContext';
import { isFourPointType } from '../../utils/polygonTypes';
import { gutterPx, stageSizePx } from '../../utils/canvasGutter';

interface CanvasStageProps {
  image: HTMLImageElement;
  scale: number;
  mode: string;
  selectedPolygon: string | null;
  onStageClick: (e: KonvaEventObject<MouseEvent>) => void;

  // Editing props
  editingPolygons: { id: string; type: PolygonEntityType; points: [number, number][] }[];
  selectedPoints: { polygonId: string; type: PolygonEntityType; idx: number }[];
  onPointSelect: (polygonId: string, type: PolygonEntityType, idx: number, extend: boolean) => void;
  onPointDragStart: (polygonId: string, type: PolygonEntityType, idx: number, pos: { x: number; y: number }) => void;
  onPointDrag: (polygonId: string, idx: number, pos: { x: number; y: number }) => void;
  onPolygonDragStart: (polygonId: string, type: PolygonEntityType, pos: { x: number; y: number }) => void;
  onPolygonDragMove: (polygonId: string, type: PolygonEntityType, pos: { x: number; y: number }) => void;
  onPolygonDrag: (pos: { x: number; y: number }) => void;
  onPointDragEnd: () => void;
  onPolygonClick: (polygonId: string, type: PolygonEntityType, extend: boolean, normalizedPos: { x: number; y: number } | null) => void;

  // Edge click → vertex insertion at the perpendicular projection.
  // Implementation gates on points.length > 4 so 4-vertex polygons stay rectangles.
  onEdgeClick?: (normalizedPos: { x: number; y: number }) => boolean;

  // Creation props
  tempPoints: [number, number][];
  selectedRackForShelf: string | null;
  awaitingRackSelection: boolean;
  onRackClickForShelf: (rackId: string) => void;
  onTempPointDrag?: (idx: number, x: number, y: number) => void;

  // Data props
  standaloneAreas: Area[];
  filteredRacks: Rack[];
  filteredShelves: Shelf[];
  filteredTables: Table[];
  filteredCheckoutZones: CheckoutZoneData[];
  getAreaPoints: (areaId: string) => [number, number][];

  // Selection props
  onSelectPointsInRect: (rect: { x1: number; y1: number; x2: number; y2: number }, extend: boolean) => void;
  onClearPointSelection: () => void;
}

const HANDLE_PX_RADIUS = 5;

const CREATION_MODES = [
  'create_standalone', 'create_rack', 'create_table', 'create_shelf',
  'create_queue', 'create_cashier', 'create_staff', 'create_order_waiting',
];

/**
 * Returns true if the cursor is over an insertable edge interior (for the
 * "copy" cursor hint). An edge is "insertable" when:
 *   - the polygon's type is NOT in FOUR_POINT_TYPES (racks/shelves/tables
 *     must stay rectangles for downstream projection)
 *   - the cursor's projection onto the edge lands inside a central band
 *     (a fixed handle-pad gap is reserved at each endpoint so hovering
 *     over a vertex never triggers the add-point cursor)
 *   - the cursor is within a small orthogonal distance of the edge line.
 * All math in screen pixels.
 */
function cursorIsOverInsertableEdge(
  polygons: { id: string; type: PolygonEntityType; points: [number, number][] }[],
  pointerPx: { x: number; y: number },
  imageSize: { width: number; height: number },
  scale: number,
  handlePxRadius: number,
): boolean {
  const HANDLE_PAD_PX = handlePxRadius * 2.5;
  const ORTH_PX = handlePxRadius * 1.8;
  for (const poly of polygons) {
    if (isFourPointType(poly.type)) continue;
    const pts = poly.points;
    if (pts.length < 2) continue;
    for (let i = 0; i < pts.length; i++) {
      const aN = pts[i];
      const bN = pts[(i + 1) % pts.length];
      const ax = aN[0] * imageSize.width * scale;
      const ay = aN[1] * imageSize.height * scale;
      const bx = bN[0] * imageSize.width * scale;
      const by = bN[1] * imageSize.height * scale;
      const ex = bx - ax;
      const ey = by - ay;
      const edgeLen = Math.hypot(ex, ey);
      // Reserved gap at each end → the central band where insertion is offered.
      const halfBand = edgeLen / 2 - HANDLE_PAD_PX;
      if (halfBand <= 0) continue;
      const dxn = ex / edgeLen;
      const dyn = ey / edgeLen;
      const mx = (ax + bx) / 2;
      const my = (ay + by) / 2;
      const rx = pointerPx.x - mx;
      const ry = pointerPx.y - my;
      const parallel = rx * dxn + ry * dyn;
      const orthogonal = -rx * dyn + ry * dxn;
      if (Math.abs(parallel) <= halfBand && Math.abs(orthogonal) <= ORTH_PX) {
        return true;
      }
    }
  }
  return false;
}

const CanvasStage: React.FC<CanvasStageProps> = ({
  image,
  scale,
  mode,
  selectedPolygon,
  onStageClick,
  editingPolygons,
  selectedPoints,
  onPointSelect,
  onPointDragStart,
  onPointDrag,
  onPolygonDragStart,
  onPolygonDragMove,
  onPolygonDrag,
  onPointDragEnd,
  onPolygonClick,
  onEdgeClick,
  tempPoints,
  selectedRackForShelf,
  awaitingRackSelection,
  onRackClickForShelf,
  onTempPointDrag,
  standaloneAreas,
  filteredRacks,
  filteredShelves,
  filteredTables,
  filteredCheckoutZones,
  getAreaPoints,
  onSelectPointsInRect,
  onClearPointSelection,
}) => {
  // Memoized so PolygonLayer's shallow-equal prop check holds across
  // selection-rect re-renders.
  const imageSize = useMemo(
    () => ({ width: image.width, height: image.height }),
    [image.width, image.height],
  );
  // Поле вокруг картинки (в пикселях картинки): вся геометрия ниже живёт в
  // координатах картинки внутри <Group x={gutter.x} y={gutter.y}>, а указатель
  // Stage приводится к ним вычитанием поля. Так вершину можно вынести за кадр.
  const gutter = useMemo(() => gutterPx(imageSize), [imageSize]);
  const stageSize = useMemo(() => stageSizePx(imageSize), [imageSize]);
  const selectionStartRef = useRef<{ x: number; y: number } | null>(null);
  const suppressClickRef = useRef(false);
  const [selectionRect, setSelectionRect] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
    visible: boolean;
  } | null>(null);

  // Vertex handle radius in SCREEN pixels — a constant, so a vertex looks the
  // same on every camera whatever the frame resolution or panel size (#430).
  // EditHandles divides it by `scale` to get image units.
  const handlePxRadius = HANDLE_PX_RADIUS;

  // Hover-edge → cursor crosshair. We deliberately keep this DOM-only (no
  // React state) so cursor moves don't trigger any re-render of the
  // (otherwise expensive) polygon layer or its handles.
  const hoverRafRef = useRef<number | null>(null);
  const pendingHoverRef = useRef<{ x: number; y: number; shift: boolean } | null>(null);
  const cursorOverEdgeRef = useRef(false);

  useEffect(() => () => {
    if (hoverRafRef.current != null) cancelAnimationFrame(hoverRafRef.current);
  }, []);

  const baseCursor = () => {
    if (CREATION_MODES.includes(mode)) {
      if (mode === 'create_shelf' && awaitingRackSelection) return 'pointer';
      return 'crosshair';
    }
    return 'default';
  };

  const canSelectPoints = useMemo(() => {
    return editingPolygons.length > 0 && !(mode && mode.startsWith('create_'));
  }, [editingPolygons.length, mode]);

  const getUnscaledPointer = (e: KonvaEventObject<MouseEvent>) => {
    const stage = e.target.getStage();
    if (!stage) return null;
    const pointer = stage.getPointerPosition();
    if (!pointer) return null;
    return { x: pointer.x / scale - gutter.x, y: pointer.y / scale - gutter.y };
  };

  const handleMouseDown = (e: KonvaEventObject<MouseEvent>) => {
    if (!canSelectPoints) return;
    if (e.evt.button !== 0) return;
    const target = e.target;
    const targetClass = target.getClassName?.();
    const isStage = target === target.getStage();
    const isImage = targetClass === 'Image';
    if (!isStage && !isImage) return;
    const pointer = getUnscaledPointer(e);
    if (!pointer) return;
    selectionStartRef.current = pointer;
    setSelectionRect({
      x: pointer.x,
      y: pointer.y,
      width: 0,
      height: 0,
      visible: true
    });
  };

  const handleMouseMove = (e: KonvaEventObject<MouseEvent>) => {
    // Selection-rectangle drag in progress — update its size.
    if (selectionStartRef.current) {
      const pointer = getUnscaledPointer(e);
      if (!pointer) return;
      const start = selectionStartRef.current;
      const x = Math.min(start.x, pointer.x);
      const y = Math.min(start.y, pointer.y);
      const width = Math.abs(pointer.x - start.x);
      const height = Math.abs(pointer.y - start.y);
      setSelectionRect({ x, y, width, height, visible: true });
      return;
    }
    // Outside edit mode or a multi-select modifier held (shift / cmd / ctrl) —
    // keep the base cursor; no edge-insert hint while the user is multi-selecting.
    const extendHeld = e.evt.shiftKey || e.evt.metaKey || e.evt.ctrlKey;
    if (!canSelectPoints || extendHeld) {
      if (cursorOverEdgeRef.current) {
        const c = e.target.getStage()?.container();
        if (c) c.style.cursor = baseCursor();
        cursorOverEdgeRef.current = false;
      }
      return;
    }
    const stage = e.target.getStage();
    const pointer = stage?.getPointerPosition();
    if (!pointer) return;
    // cursorIsOverInsertableEdge считает в экранных px от угла КАРТИНКИ
    pendingHoverRef.current = { x: pointer.x - gutter.x * scale, y: pointer.y - gutter.y * scale, shift: extendHeld };
    if (hoverRafRef.current != null) return;
    hoverRafRef.current = requestAnimationFrame(() => {
      hoverRafRef.current = null;
      const p = pendingHoverRef.current;
      pendingHoverRef.current = null;
      if (!p) return;
      const stageNow = stage;
      const container = stageNow?.container();
      if (!container) return;
      const overEdge = !p.shift && cursorIsOverInsertableEdge(
        editingPolygons, p, imageSize, scale, handlePxRadius,
      );
      if (overEdge !== cursorOverEdgeRef.current) {
        cursorOverEdgeRef.current = overEdge;
        // 'copy' renders as the system arrow with a small "+" — universally
// understood as "click to add", and distinct from the plain arrow that
// hovers over vertex handles (which signal click-to-select / drag).
container.style.cursor = overEdge ? 'copy' : baseCursor();
      }
    });
  };

  const handleMouseUp = (e: KonvaEventObject<MouseEvent>) => {
    if (!selectionStartRef.current) return;
    const pointer = getUnscaledPointer(e);
    const start = selectionStartRef.current;
    selectionStartRef.current = null;
    setSelectionRect(null);

    if (!pointer) return;
    const minX = Math.min(start.x, pointer.x);
    const minY = Math.min(start.y, pointer.y);
    const maxX = Math.max(start.x, pointer.x);
    const maxY = Math.max(start.y, pointer.y);
    const width = maxX - minX;
    const height = maxY - minY;
    if (width < 2 && height < 2) {
      // Click without rect drag. Try edge insertion first; otherwise clear.
      if (onEdgeClick) {
        const normPos = {
          x: start.x / imageSize.width,
          y: start.y / imageSize.height,
        };
        if (onEdgeClick(normPos)) {
          suppressClickRef.current = true;
          return;
        }
      }
      onClearPointSelection();
      return;
    }

    const rect = {
      x1: minX / imageSize.width,
      y1: minY / imageSize.height,
      x2: maxX / imageSize.width,
      y2: maxY / imageSize.height
    };
    onSelectPointsInRect(rect, e.evt.shiftKey);
    suppressClickRef.current = true;
  };

  const handleClick = (e: KonvaEventObject<MouseEvent>) => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    onStageClick(e);
  };

  const handleMouseLeave = (e: KonvaEventObject<MouseEvent>) => {
    if (cursorOverEdgeRef.current) {
      const c = e.target.getStage()?.container();
      if (c) c.style.cursor = baseCursor();
      cursorOverEdgeRef.current = false;
    }
  };

  return (
    <Stage
      width={stageSize.width * scale}
      height={stageSize.height * scale}
      scale={{ x: scale, y: scale }}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      style={{ cursor: baseCursor() }}
    >
      <Layer>
        {/* Поле вокруг кадра: тёмный фон, чтобы граница картинки читалась */}
        <Rect x={0} y={0} width={stageSize.width} height={stageSize.height} fill="#1f1f1f" listening={false} />
        <Group x={gutter.x} y={gutter.y}>
        <KonvaImage image={image} />

        <PolygonLayer
          imageSize={imageSize}
          scale={scale}
          handlePxRadius={handlePxRadius}
          mode={mode}
          selectedPolygon={selectedPolygon}
          editingPolygons={editingPolygons}
          selectedPoints={selectedPoints}
          onPointSelect={onPointSelect}
          onPointDragStart={onPointDragStart}
          onPointDrag={onPointDrag}
          onPolygonDragStart={onPolygonDragStart}
          onPolygonDragMove={onPolygonDragMove}
          onPolygonDrag={onPolygonDrag}
          onPointDragEnd={onPointDragEnd}
          onPolygonClick={onPolygonClick}
          standaloneAreas={standaloneAreas}
          filteredRacks={filteredRacks}
          filteredShelves={filteredShelves}
          filteredTables={filteredTables}
          filteredCheckoutZones={filteredCheckoutZones}
          getAreaPoints={getAreaPoints}
          awaitingRackSelection={awaitingRackSelection}
          onRackClickForShelf={onRackClickForShelf}
        />

        <CreationOverlay
          mode={mode}
          scale={scale}
          tempPoints={tempPoints}
          selectedRackForShelf={selectedRackForShelf}
          onTempPointDrag={onTempPointDrag}
        />

        {selectionRect?.visible && (
          <Rect
            x={selectionRect.x}
            y={selectionRect.y}
            width={selectionRect.width}
            height={selectionRect.height}
            stroke="rgba(0, 153, 255, 0.9)"
            strokeWidth={1 / scale}
            dash={[6 / scale, 4 / scale]}
            fill="rgba(0, 153, 255, 0.15)"
            listening={false}
          />
        )}
        </Group>
      </Layer>
    </Stage>
  );
};

export default CanvasStage;
